import {
  ApolloQueryResult,
  QueryHookOptions,
  QueryResult,
} from '@apollo/client';
import { IonInfiniteScroll, IonInfiniteScrollContent } from '@ionic/react';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../util/logger';
import { Exact, Maybe } from '../graphql/generated';

export interface QueryInput {
  limit?: Maybe<number> | undefined;
  offset?: Maybe<number> | undefined;
}

interface SearchQueryInput extends QueryInput {
  searchTerm?: string;
}

interface PaginatedQueryResult {
  total?: Maybe<number> | undefined;
  items?: unknown[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GQLQueryOptions = QueryHookOptions<any, any>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface InfiniteScrollOptions<Query = any, Input = any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useQuery: (baseOptions: GQLQueryOptions) => QueryResult<
    Query,
    Exact<{
      input: Input;
    }>
  >;
  input?: Input;
  queryOptions?: Omit<GQLQueryOptions, 'variables'>;
  axis?: 'horizontal' | 'vertical';
  onGetMore?: (result: ApolloQueryResult<Query>) => void;
}

export interface InfiniteScrollResult<Query> {
  infiniteScroll: ReactElement;
  result: QueryResult<Query>;
  totalItems: number;
  items: unknown[];
}

export interface InfiniteScrollSearchResult<Query>
  extends InfiniteScrollResult<Query> {
  search: (searchTerm: string | undefined) => void;
  searchTerm?: string;
}

function useSearchListQueryWithInfiniteScroll<Query, Input>(
  options: InfiniteScrollOptions<Query, Input>
): InfiniteScrollSearchResult<Query> {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  const mergedOptions = useMemo<
    InfiniteScrollOptions<Query, SearchQueryInput>
  >(() => {
    return {
      ...options,
      input: {
        ...options.input,
        searchTerm,
      },
    } as unknown as InfiniteScrollOptions<Query, SearchQueryInput>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(options), searchTerm]);

  const result = useListQueryWithInfiniteScroll(mergedOptions);

  return {
    ...result,
    search: setSearchTerm,
    searchTerm,
  };
}

function useListQueryWithInfiniteScroll<Query, Input extends QueryInput>(
  options: InfiniteScrollOptions<Query, Input>
): InfiniteScrollResult<Query> {
  const { queryOptions, input, useQuery, axis, onGetMore } = options ?? {};
  const { i18n } = useTranslation();

  useEffect(() => {
    if (queryOptions?.fetchPolicy === 'no-cache') {
      logger.warn(
        'Selecting no-cache will prevent gql ui components from subscribing to the cache and break infinite scroll. Use network-only instead.'
      );
    }
  }, [queryOptions?.fetchPolicy]);

  const mergedInput = useMemo<Input>(() => {
    return {
      limit: 25,
      ...input,
    } as Input;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(input)]);

  const infiniteScrollRef = useRef<HTMLIonInfiniteScrollElement | null>(null);

  const gqlResult = useQuery({
    variables: {
      input: mergedInput,
    },
    ...queryOptions,
  });

  const resultObj = Object.values(
    gqlResult.data ?? {}
  )[0] as PaginatedQueryResult;

  const totalItems = resultObj?.total ?? 0;
  const itemsLength = resultObj?.items?.length ?? 0;

  const { fetchMore } = gqlResult;

  const getMore = useCallback(async () => {
    const result = await fetchMore({
      variables: {
        input: {
          ...mergedInput,
          offset: itemsLength,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      },
    });
    infiniteScrollRef.current?.complete();
    if (typeof onGetMore === 'function') {
      onGetMore(result);
    }
  }, [fetchMore, itemsLength, mergedInput, onGetMore]);

  // logic for horizontal scrolling
  const observer = useRef<IntersectionObserver | null>(null);
  const observableRef = useCallback(
    (node: HTMLIonInfiniteScrollElement) => {
      infiniteScrollRef.current = node;
      if (axis !== 'horizontal') {
        return;
      }

      if (gqlResult.loading) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && itemsLength < totalItems) {
          getMore();
        }
      });

      if (node !== null) {
        observer.current.observe(node);
      }
    },
    [axis, getMore, gqlResult.loading, itemsLength, totalItems]
  );

  const result = useMemo<InfiniteScrollResult<Query>>(() => {
    return {
      getMore,
      result: gqlResult,
      totalItems,
      items: resultObj?.items ?? [],
      infiniteScroll: (
        <IonInfiniteScroll
          ref={observableRef}
          onIonInfinite={getMore}
          className="ion-padding"
          disabled={itemsLength === totalItems}
          threshold={'25%'}
        >
          <IonInfiniteScrollContent loadingText={i18n.t('common:loading')} />
        </IonInfiniteScroll>
      ),
    } as unknown as InfiniteScrollResult<Query>;
  }, [
    getMore,
    gqlResult,
    totalItems,
    resultObj?.items,
    observableRef,
    itemsLength,
    i18n,
  ]);

  return result;
}

export { useSearchListQueryWithInfiniteScroll, useListQueryWithInfiniteScroll };
